/* eslint-disable react/jsx-indent */
import type { FC, ReactElement } from 'react';
import React, { useContext } from 'react';

import { ModalServiceContext, RicosContext, ShortcutsContext } from './../ricos-context';
import type { ModalService } from './../ricos-modals';
import type { LocalizedDisplayData, ShortcutDataProvider } from './../ricos-types';
import { SettingsPanelHeader } from './../ui-components';

import { ShortcutsSheet } from './shortcuts-sheet';
import styles from './ShortcutsDialog-scss';

const SHORTCUT_SHEET_MODAL_ID = 'shortcuts-help';

const renderButton = (key: LocalizedDisplayData['keyCombinationText']) => (
  <span key={key} className={styles.button}>
    {key}
  </span>
);

const renderShorcut = (
  keys: LocalizedDisplayData['keyCombinationText'] | LocalizedDisplayData['keyCombinationText'][]
) =>
  Array.isArray(keys)
    ? keys.map((k, i) => (
        <span key={i} className={styles.shortcut}>
          {k.split(' ').map(renderButton)}
        </span>
      ))
    : keys.split(' ').map(renderButton);

export const ShortcutsDialog: FC = () => {
  const ricosContext = useContext(RicosContext);
  const { t, platform } = ricosContext;
  const shortcuts = useContext(ShortcutsContext) as ShortcutDataProvider;
  const sheet = new ShortcutsSheet(shortcuts, ricosContext);
  const sortedDisplayData = sheet.getDisplayData();
  const helpShortcutKeys = shortcuts.getShortcutDisplayData(
    'OpenShortcutsSheet',
    t,
    platform
  ).keyCombinationText;

  const title = `${t('ShortcutsSheet_Title')} ${helpShortcutKeys}`;

  const onClose = () => modalService.closeModal(SHORTCUT_SHEET_MODAL_ID);

  const modalService: ModalService = useContext(ModalServiceContext);

  return (
    <>
      <SettingsPanelHeader title={title} onClose={onClose} />
      <hr className={styles.titleBorder} />
      <table className={styles.keymapTable}>
        <tbody>
          {Object.keys(sortedDisplayData).reduce((memo, group) => {
            const displayDataItems = sortedDisplayData[group];

            memo.push(
              <tr key={group}>
                <td className={styles.group}>{t(group)}</td>
              </tr>
            );
            displayDataItems.forEach(
              item =>
                item.name !== 'Empty' &&
                memo.push(
                  <tr key={item.name}>
                    <td className={styles.keyEntry}>
                      <span className={styles.description}>{item.description}</span>
                      <span style={{ flexGrow: 0 }}>&nbsp;</span>
                      <span className={styles.keys}>{renderShorcut(item.keyCombinationText)}</span>
                    </td>
                  </tr>
                )
            );

            return memo;
          }, [] as ReactElement[])}
        </tbody>
      </table>
    </>
  );
};
